import { useCallback, useEffect, useRef, useState } from 'react';
import { startRegistration } from '@simplewebauthn/browser';

import { useTracker } from '@archipro-website/tracker';
import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { cn } from '@rocco/utils/cn';
import {
    PASSKEY_REGISTER_VERIFY_API_ROUTE,
    type PasskeyRegisterVerifyAction,
} from '~/routes/remix-api.user.passkey-register-verify';
import {
    markPasskeyEnrolled,
    markPasskeyOfferSkipped,
} from '~/modules/user/passkey/passkey-device-hint';
import { passkeyDebugDetail } from '~/modules/user/passkey/passkey-diagnostics';
import {
    isMintStale,
    mintEnrolmentOptions,
    type PasskeyEnrolmentMint,
} from '~/modules/user/passkey/passkey-enrol-mint';
import type { AuthSource } from '~/modules/user/type';
import { derivePlatformBiometricLabel } from '~/modules/user/passkey/platform-label';
import { startMinimumHold } from '~/modules/user/passkey/minimum-hold';
import { PASSKEY_SPEED_COPY } from './auth-intent-copy';
import { GateHeading, GateSubcopy } from './GateHeading';
import { GateStickyFooter } from './GateStickyFooter';
import { SecureSignInGlyph } from './SecureSignInGlyph';

export interface SecureSignInOfferProps {
    /**
     * The challenge this ask exists to spend, minted before the step was
     * entered. Holding one is what makes the ask worth rendering: the server
     * has already agreed to enrol this member on this device.
     */
    mint: PasskeyEnrolmentMint;
    /**
     * The gate this ask is standing in. It rides every event as `Source`, so
     * without it enrolments from the download, save and bulk gates all report
     * as the enquiry gate and no slice of passkey adoption by surface is
     * trustworthy. The hosts' own skip events already carry the real value.
     */
    authSource: AuthSource;
    /** First name stored in the device hint for the step-0 greeting (BC-4832). */
    firstName?: string;
    /**
     * Who is enrolling, for the hint's owner. Passed in rather than read from
     * the session here: this step is a leaf, and reaching for the user module
     * pulls the passkey support probe into the graph of anyone who renders it.
     */
    memberID?: number;
    /**
     * Fired the moment a credential is stored, ahead of the success frame and
     * of `onDone`.
     *
     * Between those two the ask has been answered but has not said so, and a
     * host that reads walking away as a decline would otherwise have to infer
     * what happened from the device hint — which cannot distinguish this
     * ask's enrolment from one already on the browser.
     */
    onEnrolled?: () => void;
    /** Called when the offer resolves: enrolled, failed, or skipped. */
    onDone: (enrolled: boolean) => void;
    className?: string;
}

/** Platform-appropriate CTA label; the design shows the Face ID wording. */
const deriveCtaLabel = (): string => {
    const label = derivePlatformBiometricLabel();

    return label ? `Enable ${label}` : 'Enable Secure Sign-In';
};

/** Every ceremony animates for at least this long, however fast the API is. */
const MIN_CEREMONY_MS = 350;

/** How long the drawn checkmark stays before the flow moves on. */
const SUCCESS_DISPLAY_MS = 650;

/** Best-effort device label recorded against the credential. */
const deriveDeviceName = (): string => {
    if (typeof navigator === 'undefined') return 'This device';
    const ua = navigator.userAgent;
    const browser = /Edg\//.test(ua)
        ? 'Edge'
        : /Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : /Firefox\//.test(ua)
              ? 'Firefox'
              : 'Browser';
    const platform = /iPhone/.test(ua)
        ? 'iPhone'
        : /iPad/.test(ua)
          ? 'iPad'
          : /Android/.test(ua)
            ? 'Android'
            : /Mac/.test(ua)
              ? 'macOS'
              : /Windows/.test(ua)
                ? 'Windows'
                : 'this device';

    return `${browser} on ${platform}`;
};

/**
 * "Enable Secure Sign-In on this device" step (BC-4831, Figma 3177:16188 /
 * 3177:16200). Shown once per the device-hint cadence, right after auth and
 * before the flow's success screen — always skippable, and a failure resolves
 * the flow rather than blocking it.
 *
 * The challenge arrives already minted, so reaching this step is itself the
 * server's agreement to enrol: there is no state in which the ask is on screen
 * but unusable, and none in which it renders nothing while it finds out. The
 * decision that mints it — {@link usePasskeyEnrolOffer} — is where a refusal is
 * absorbed.
 */
export const SecureSignInOffer = ({
    mint,
    firstName,
    memberID,
    authSource,
    onEnrolled,
    onDone,
    className,
}: SecureSignInOfferProps) => {
    const tracker = useTracker();
    // The glyph sits directly above the pinned actions on the sheet, where
    // the desktop card's flexible spacer collapses to nothing.
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const [busy, setBusy] = useState(false);
    const [succeeded, setSucceeded] = useState(false);
    const [error, setError] = useState<string>();

    const mintRef = useRef<PasskeyEnrolmentMint | undefined>(mint);

    const impressionLogged = useRef(false);
    useEffect(() => {
        if (impressionLogged.current) return;
        impressionLogged.current = true;
        tracker.log('PasskeyEnrolOfferShown', {
            url: new URL(window.location.href),
            data: { Source: authSource },
        });
    }, [tracker, authSource]);

    const handleEnable = useCallback(async () => {
        setBusy(true);
        setError(undefined);
        const settle = startMinimumHold(MIN_CEREMONY_MS);
        try {
            // The mint is single-use (verify consumes its challenge) — take it
            // while fresh, otherwise mint again. A reader can sit on this step
            // for longer than the challenge lives.
            const held = mintRef.current;
            mintRef.current = undefined;
            const { optionsJSON } =
                held && !isMintStale(held)
                    ? held
                    : await mintEnrolmentOptions();

            const registration = await startRegistration({
                optionsJSON: JSON.parse(optionsJSON),
            });

            const verifyBody = new FormData();
            verifyBody.append('credential', JSON.stringify(registration));
            verifyBody.append('deviceName', deriveDeviceName());
            const verifyResponse = await fetch(
                PASSKEY_REGISTER_VERIFY_API_ROUTE,
                { method: 'POST', body: verifyBody },
            );
            const verified =
                (await verifyResponse.json()) as PasskeyRegisterVerifyAction;
            if (!verified.ok) {
                throw new Error(verified.message);
            }

            markPasskeyEnrolled({
                // From the session that authorised the write, so the hint knows
                // whose passkey it is and stops speaking for other accounts.
                memberID: verified.memberID,
                firstName,
                rpId: window.location.hostname.replace(/^www\./, ''),
            });
            onEnrolled?.();
            tracker.log('PasskeyEnrolled', {
                url: new URL(window.location.href),
                data: { Source: authSource },
            });
            await settle();
            // Let the checkmark draw and land before the flow moves on.
            const showSuccess = startMinimumHold(SUCCESS_DISPLAY_MS);
            setSucceeded(true);
            await showSuccess();
            onDone(true);
        } catch (err) {
            // InvalidStateError = this authenticator already holds a passkey
            // for this account, because `excludeCredentials` named it. That is
            // the state the offer exists to reach, so it is a success with
            // nothing left to do — not a failure to report.
            //
            // It is reachable whenever the cadence hint and the authenticator
            // disagree, and they disagree by construction: the hint is in
            // localStorage, scoped to the origin, while the credential is
            // scoped to the relying party id and shared across its subdomains.
            // Every new environment on a subdomain therefore re-offers
            // enrolment to someone who enrolled long ago. Recording the hint
            // here is also what stops it asking again.
            if (err instanceof Error && err.name === 'InvalidStateError') {
                markPasskeyEnrolled({
                    // Named, because a hint with an unknown owner suppresses
                    // the offer for everyone who later signs in on this
                    // browser, not just for the member who reached this.
                    memberID,
                    firstName,
                    rpId: window.location.hostname.replace(/^www\./, ''),
                });
                onEnrolled?.();
                tracker.log('PasskeyEnrolled', {
                    url: new URL(window.location.href),
                    data: { Source: authSource, ExtraData: 'already_enrolled' },
                });
                await settle();
                onDone(true);
                return;
            }
            // NotAllowedError = the user dismissed the platform prompt —
            // treat as a skip, not an error.
            if (err instanceof Error && err.name === 'NotAllowedError') {
                markPasskeyOfferSkipped();
                tracker.log('PasskeyEnrolSkipped', {
                    url: new URL(window.location.href),
                    data: { Source: authSource, ExtraData: 'prompt_dismissed' },
                });
                await settle();
                onDone(false);
                return;
            }
            // Server failures carry client-safe messages (account policy,
            // expired challenge) — show them. WebAuthn API errors
            // (SecurityError = rp id / origin mismatch, etc.) get the generic
            // line; the raw error goes to the console for QA.
            console.warn('[SecureSignIn] enrolment failed', err);
            const isServerMessage =
                err instanceof Error &&
                err.name === 'Error' &&
                err.message !== '';
            await settle();
            setError(
                isServerMessage
                    ? err.message
                    : (passkeyDebugDetail(err) ??
                          'Secure Sign-In could not be enabled on this device.'),
            );
            setBusy(false);
        }
    }, [firstName, memberID, onEnrolled, onDone, tracker, authSource]);

    const handleSkip = useCallback(() => {
        markPasskeyOfferSkipped();
        tracker.log('PasskeyEnrolSkipped', {
            url: new URL(window.location.href),
            data: { Source: authSource },
        });
        onDone(false);
    }, [onDone, tracker, authSource]);

    return (
        <div
            className={cn(
                'mx-auto flex min-h-0 w-full max-w-90.75 flex-1 flex-col',
                className,
            )}
        >
            <GateHeading className="mt-3">
                Enable Secure Sign-In on this device
            </GateHeading>
            <GateSubcopy className="leading-normal">
                {PASSKEY_SPEED_COPY}
            </GateSubcopy>
            <div className={cn('mt-10.5', isMobile && 'mb-10')}>
                <SecureSignInGlyph
                    state={succeeded ? 'success' : busy ? 'busy' : 'idle'}
                />
            </div>
            {error && (
                <p
                    role="alert"
                    className="mt-4 text-center text-[0.8125rem] text-destructive"
                >
                    {error}
                </p>
            )}
            <div className="flex-1" />
            <GateStickyFooter desktopClassName="mb-9">
                <button
                    type="button"
                    disabled={busy}
                    onClick={handleEnable}
                    className="flex h-14 w-full items-center justify-center rounded-full bg-[#181818] text-[1.125rem] font-medium tracking-[-0.02em] text-[#f3f3f3] shadow-[0_0.1875rem_0.8125rem_0_rgba(0,0,0,0.25),0_1.25rem_2.125rem_0_rgba(0,0,0,0.25)] transition-colors hover:bg-[#2c2c2c] disabled:cursor-not-allowed"
                >
                    {succeeded
                        ? 'Enabled.'
                        : busy
                          ? 'Authenticating.'
                          : deriveCtaLabel()}
                </button>
                <button
                    type="button"
                    disabled={busy}
                    onClick={handleSkip}
                    className="mt-6 w-full text-center text-[1.0625rem] tracking-[-0.02em] text-[#44443f]"
                >
                    Skip for now
                </button>
            </GateStickyFooter>
        </div>
    );
};
